import * as React from 'react';
import {
  Box,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppsIcon from '@mui/icons-material/Apps';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import InboxIcon from '@mui/icons-material/Inbox';
import MemoryIcon from '@mui/icons-material/Memory';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import BarChartIcon from '@mui/icons-material/BarChart';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';
import { useClerk } from '@clerk/clerk-react';
import LanguageSelector from './LanguageSelector';
import CargoVistaLogo from './CargoVistaLogo';
import { usePermissions, Permissions } from '../../contexts/PermissionContext';

const drawerWidth = 256;

const navButtonSx = {
  mx: '8px',
  px: '12px',
  py: '8px',
  gap: '10px',
  borderRadius: '8px',
  color: 'text.secondary',
  '& .MuiListItemIcon-root': {
    color: 'text.secondary',
    minWidth: 'auto',
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  '& .MuiListItemText-root': {
    margin: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: 14,
    fontWeight: 500,
  },
  '&:hover': {
    bgcolor: 'primary.25',
    color: 'text.primary',
    '& .MuiListItemIcon-root': {
      color: 'text.primary',
    },
  },
  '&.Mui-selected': {
    backgroundColor: 'primary.50',
    color: 'primary.800',
    '&:hover': {
      backgroundColor: 'primary.100',
    },
    '& .MuiListItemIcon-root': {
      color: 'primary.main',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  },
} as const;

interface NavItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  section?: 'workspace' | 'admin';
}

interface DrawerProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

const Drawer: React.FC<DrawerProps> = ({ mobileOpen, onDrawerToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    if (isMobile) {
      onDrawerToggle();
    }
  };

  const navItems: NavItem[] = [
    { text: t('common.dashboard'), path: '/', icon: <AppsIcon />, section: 'workspace' },
    ...(hasPermission(Permissions.PROJECT_READ)
      ? [{ text: t('common.projects'), path: '/projects', icon: <FolderIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.DOCUMENT_UPLOAD)
      ? [{ text: t('common.uploads'), path: '/upload', icon: <CloudUploadIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.PROJECT_READ)
      ? [{ text: t('common.processing'), path: '/processing', icon: <MemoryIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.DOCUMENT_REFILE)
      ? [{ text: t('common.unfiled'), path: '/unfiled', icon: <InboxIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.CONFLICT_RESOLVE) && hasPermission(Permissions.PROJECT_READ)
      ? [{ text: t('common.conflicts'), path: '/conflicts', icon: <GppMaybeIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.ANALYSIS_RUN)
      ? [{ text: t('common.analyses'), path: '/analyses', icon: <BarChartIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.PUBLICATION_MANAGE)
      ? [{ text: t('common.publications'), path: '/publications', icon: <SendIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.EMAIL_RELEASE)
      ? [{ text: t('common.emailIngestion'), path: '/email-ingestion', icon: <MailOutlineIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.OPS_RECOVER)
      ? [{ text: t('common.operations'), path: '/operations', icon: <MonitorHeartIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.SETTINGS_MANAGE)
      ? [{ text: t('common.settings'), path: '/settings', icon: <SettingsIcon />, section: 'workspace' as const }]
      : []),
    ...(hasPermission(Permissions.USER_MANAGE)
      ? [{ text: t('common.users'), path: '/users', icon: <PeopleIcon />, section: 'admin' as const }]
      : []),
    ...(hasPermission(Permissions.ROLE_MANAGE)
      ? [{ text: t('common.roles'), path: '/roles', icon: <AdminPanelSettingsIcon />, section: 'admin' as const }]
      : []),
    ...(hasPermission(Permissions.ADMIN_READ_AUDIT)
      ? [{ text: t('auditLog.title'), path: '/audit-log', icon: <HistoryIcon />, section: 'admin' as const }]
      : []),
  ];

  const isSelected = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderSection = (label: string, items: NavItem[]) => (
    <>
      {label && <Typography
        component="p"
        sx={{
          px: '20px',
          pt: '8px',
          pb: '6px',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          opacity: 0.7,
        }}
      >
        {label}
      </Typography>}
      {items.map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            component={Link}
            to={item.path}
            selected={isSelected(item.path)}
            onClick={isMobile ? onDrawerToggle : undefined}
            sx={navButtonSx}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          px: '20px',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <CargoVistaLogo />
        </Box>
      </Box>
      <List sx={{ flex: 1, pt: '12px', pb: '8px', overflowY: 'auto' }}>
        {renderSection('', navItems.filter((i) => i.section !== 'admin'))}
        {navItems.some((i) => i.section === 'admin') && (
          <>
            <Divider sx={{ my: '12px', mx: '20px' }} />
            {renderSection(t('common.administration'), navItems.filter((i) => i.section === 'admin'))}
          </>
        )}
      </List>

      <Divider />

      <Box sx={{ p: '8px' }}>
        <List disablePadding>
          <ListItem disablePadding sx={{ mb: '4px' }}>
            <ListItemButton
              sx={{
                ...navButtonSx,
                '& .MuiSvgIcon-root': { fontSize: 18 },
              }}
            >
              <LanguageSelector variant="full" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={navButtonSx}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('common.logOut')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </MuiDrawer>
      ) : (
        <MuiDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </MuiDrawer>
      )}
    </Box>
  );
};

export default Drawer;
